import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supportTicketService } from 'api/services/SupportTicketService';
import type { SupportTicketViewModel } from 'api/types/support-ticket';
import Button from 'components/Button';
import SbEmptyState from 'components/layout/SbEmptyState';

const PAGE_SIZE = 10;

export interface SupportTicketListProps {
  detailPath?: (id: string) => string;
}

const SupportTicketList: React.FC<SupportTicketListProps> = ({
  detailPath = (id) => `/teknik-destek/ticket/${id}`,
}) => {
  const [tickets, setTickets] = useState<SupportTicketViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await supportTicketService.getMyTickets({ pagination: { pageNumber: page, pageSize: PAGE_SIZE } });
      if (result.isSuccess && result.data) {
        const list = Array.isArray(result.data) ? result.data : [];
        setTickets(list);
        const total = result.pagination?.totalPageCount ?? 1;
        setTotalPages(total);
      } else {
        setTickets([]);
        setError(result.message ?? 'Talepler yüklenemedi.');
      }
    } catch (err) {
      setTickets([]);
      setError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(currentPage);
  }, [currentPage, loadPage]);

  const pageNumbers = Array.from({ length: Math.max(0, totalPages) }, (_, i) => i + 1);

  const visibleTickets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter(
      (t) =>
        t.id.toLowerCase().includes(q) ||
        (t.supportCategoryName ?? '').toLowerCase().includes(q) ||
        (t.supportStatusName ?? '').toLowerCase().includes(q)
    );
  }, [tickets, searchQuery]);

  return (
    <div className="sb-panel-card h-100 d-flex flex-column">
      <div className="sb-panel-card__header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <h2 className="sb-panel-card__title mb-0">Taleplerim</h2>
        <div className="sb-inline-search mb-0" style={{ minWidth: 'min(100%, 14rem)' }}>
          <div className="sb-inline-search__field py-1 px-2">
            <i className="fas fa-search sb-inline-search__icon" aria-hidden />
            <input
              type="search"
              className="form-control sb-inline-search__input"
              placeholder="Taleplerimde ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Taleplerimde ara"
            />
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        {error && (
          <div className="alert alert-warning rounded-0 mb-0 py-2" role="alert">
            {error}
          </div>
        )}
        {loading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
          </div>
        ) : (
          <>
            <ul className="list-group list-group-flush">
              {visibleTickets.map((t) => (
                <li key={t.id} className="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    <Link to={detailPath(t.id)} className="fw-medium text-decoration-none">
                      #{t.id.slice(0, 8)}… – {t.supportCategoryName ?? 'Destek'}
                    </Link>
                    <small className="text-muted d-block">
                      {t.supportStatusName ?? 'Beklemede'} · {t.createdAtUtc ? new Date(t.createdAtUtc).toLocaleDateString('tr-TR') : ''}
                    </small>
                  </div>
                  <Link to={detailPath(t.id)} className="btn btn-sm btn-outline-primary">
                    Detay
                  </Link>
                </li>
              ))}
            </ul>
            {!loading && tickets.length === 0 && (
              <div className="p-3">
                <SbEmptyState
                  iconClass="fas fa-headset"
                  title="Henüz destek talebiniz yok"
                  description="Oluşturduğunuz destek talepleri burada görünecek."
                />
              </div>
            )}
            {!loading && tickets.length > 0 && visibleTickets.length === 0 && (
              <div className="text-muted text-center py-4 px-3">Aramanızla eşleşen talep yok.</div>
            )}
            {totalPages > 1 && (
              <nav className="p-3 border-top" aria-label="Sayfalar">
                <ul className="pagination pagination-sm mb-0 justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <Button
                      className="page-link"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Önceki
                    </Button>
                  </li>
                  {pageNumbers.map((num) => (
                    <li key={num} className={`page-item ${currentPage === num ? 'active' : ''}`}>
                      <Button className="page-link" onClick={() => setCurrentPage(num)}>
                        {num}
                      </Button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <Button
                      className="page-link"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Sonraki
                    </Button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupportTicketList;
